import './ImportDialog.css';
import '../../pages/import/EnrichWizard.css';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  HiOutlineCloudUpload,
  HiOutlineDocumentText,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineExclamationCircle,
  HiOutlineCheckCircle,
  HiOutlineStar,
} from 'react-icons/hi';
import {
  HiXMark,
  HiCheck,
  HiStar,
  HiArrowPath,
  HiChevronUpDown,
  HiArrowLeft,
} from 'react-icons/hi2';
import * as XLSX from 'xlsx-js-style';
import Spinner from '../Spinner/Spinner';
import useAuthStore from '../../stores/authStore';
import {
  getImportHistory,
  getUnmatchedStores,
  getUploadDedupResponse,
  uploadCSVDirect,
  validateCSVMapping,
  dedupStores,
  enrichStores,
  submitFeedback,
} from '../../services/importService';

interface ImportRecord {
  id: string;
  original_filename: string;
  file_size_bytes: number | null;
  upload_timestamp: string;
  row_count: number;
  matched_count: number | null;
  unmatched_count: number | null;
  status: 'processing' | 'completed' | 'failed';
  uploader_id: string;
  uploader?: { id: string; name: string | null; email: string } | null;
  feedback?: Array<{ id: string; rating: number; feedback_text: string | null }>;
}

interface ImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete?: () => void;
  brandName: string;
  brandId: string;
  isUltimateParent: boolean;
}

interface FieldOption {
  id: string;
  label: string;
  fieldType: 'predefined' | 'dynamic';
  dataType: string;
  required?: boolean;
  matchOnly?: boolean;
}

interface MatchResults {
  matchRate: number;
  matched: number;
  unmatched: number;
  totalRows: number;
  enrichResults?: {
    updated_count: number;
    predefined_enriched: number;
    dynamic_enriched: number;
    failed_count: number;
  };
}

type WizardStep = 1 | 2 | 3 | 4;
type DialogView = 'history' | 'wizard';

const toTitleCase = (str: string) =>
  str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const SYSTEM_FIELDS: FieldOption[] = [
  // Match Fields (required)
  { id: 'brand_name', label: 'Brand Name', fieldType: 'predefined', dataType: 'string', required: true, matchOnly: true },
  { id: 'address', label: 'Address', fieldType: 'predefined', dataType: 'string', required: true, matchOnly: true },
  { id: 'zipcode', label: 'Zip Code', fieldType: 'predefined', dataType: 'string', required: true, matchOnly: true },
  // POI
  { id: 'customer_id', label: 'Customer ID', fieldType: 'predefined', dataType: 'string' },
  { id: 'sg_id', label: 'SG ID', fieldType: 'predefined', dataType: 'string' },
  { id: 'site', label: 'Site', fieldType: 'predefined', dataType: 'string' },
  { id: 'store_number', label: 'Store Number', fieldType: 'predefined', dataType: 'string', matchOnly: true },
  { id: 'latitude', label: 'Latitude', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'longitude', label: 'Longitude', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'opened_on', label: 'Opened On', fieldType: 'predefined', dataType: 'date' },
  { id: 'closed_on', label: 'Closed On', fieldType: 'predefined', dataType: 'date' },
  { id: 'status', label: 'Status', fieldType: 'predefined', dataType: 'string' },
  { id: 'type', label: 'Type', fieldType: 'predefined', dataType: 'string' },
  { id: 'channel', label: 'Channel', fieldType: 'predefined', dataType: 'string' },
  { id: 'purchek_install_date', label: 'Purchek Install Date', fieldType: 'predefined', dataType: 'date' },
  { id: 'containment_install_date', label: 'Containment Install Date', fieldType: 'predefined', dataType: 'date' },
  { id: 'cart_manager_install_date', label: 'Cart Manager Install Date', fieldType: 'predefined', dataType: 'date' },
  { id: 'camera_install_date', label: 'Camera Install Date', fieldType: 'predefined', dataType: 'date' },
  { id: 'subscription_start_date', label: 'Subscription Start Date', fieldType: 'predefined', dataType: 'date' },
  { id: 'paired_wheel_install_date', label: 'Paired Wheel Install Date', fieldType: 'predefined', dataType: 'date' },
  { id: 'combo_start_date', label: 'Combo Start Date', fieldType: 'predefined', dataType: 'date' },
  { id: 'lifetime_value', label: 'Lifetime Value', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'rank', label: 'Rank', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'cart_manager_rank', label: 'Cart Manager Rank', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'purchek_rank', label: 'Purchek Rank', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'containment_rank', label: 'Containment Rank', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'last_purchase', label: 'Last Purchase', fieldType: 'predefined', dataType: 'date' },
  { id: 'first_purchase', label: 'First Purchase', fieldType: 'predefined', dataType: 'date' },
  { id: 'risk_index', label: 'Risk Index', fieldType: 'predefined', dataType: 'string' },
  { id: 'risk_score', label: 'Risk Score', fieldType: 'predefined', dataType: 'string' },
  { id: 'shrink_incident_count', label: 'Shrink Incident Count', fieldType: 'predefined', dataType: 'string' },
  { id: 'shrink_incident_value', label: 'Shrink Incident Value', fieldType: 'predefined', dataType: 'string' },
  // Geo
  { id: 'city', label: 'City', fieldType: 'predefined', dataType: 'string' },
  { id: 'state', label: 'State', fieldType: 'predefined', dataType: 'string' },
  { id: 'state_code', label: 'State Code', fieldType: 'predefined', dataType: 'string' },
  { id: 'country', label: 'Country', fieldType: 'predefined', dataType: 'string' },
  { id: 'county', label: 'County', fieldType: 'predefined', dataType: 'string' },
  { id: 'containment_required', label: 'Containment Required', fieldType: 'predefined', dataType: 'boolean' },
  { id: 'penalty_for_cart_owner', label: 'Penalty for Cart Owner', fieldType: 'predefined', dataType: 'boolean' },
  { id: 'law_summary', label: 'Law Summary', fieldType: 'predefined', dataType: 'string' },
  { id: 'law_source', label: 'Law Source', fieldType: 'predefined', dataType: 'string' },
  { id: 'total_crime_incidents', label: 'Total Crime Incidents', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'crime_rate', label: 'Crime Rate', fieldType: 'predefined', dataType: 'string' },
  { id: 'crime_data', label: 'Crime Data', fieldType: 'predefined', dataType: 'string' },
  { id: 'no_of_cold_days', label: 'No. of Cold Days', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'no_of_hot_days', label: 'No. of Hot Days', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'annual_precipitation', label: 'Annual Precipitation', fieldType: 'predefined', dataType: 'numeric' },
  // Nearby Penetration
  { id: 'total_neighbors', label: 'Total Neighbors', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'purchek_customer_count', label: 'Purchek Customer Count', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'containment_customer_count', label: 'Containment Customer Count', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'combo_customer_count', label: 'Combo Customer Count', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'penetration', label: 'Penetration', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'penetration_purchek', label: 'Penetration Purchek', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'penetration_containment', label: 'Penetration Containment', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'penetration_combo', label: 'Penetration Combo', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'penetration_paired_wheel', label: 'Penetration Paired Wheel', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'plaza_cm_customer_count', label: 'Plaza CM Customer Count', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'plaza_cluster', label: 'Plaza Cluster', fieldType: 'predefined', dataType: 'string' },
  { id: 'paired_wheel_customer_count', label: 'Paired Wheel Customer Count', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'shoplifting_amount_usd', label: 'Shoplifting Amount (USD)', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'shoplifting_incident_count', label: 'Shoplifting Incident Count', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'highway_proximity', label: 'Highway Proximity', fieldType: 'predefined', dataType: 'string' },
  { id: 'total_density', label: 'Total Density', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'busstop_proximity', label: 'Bus Stop Proximity', fieldType: 'predefined', dataType: 'numeric' },
  // Video
  { id: 'total_crime_amount_usd', label: 'Total Crime Amount (USD)', fieldType: 'predefined', dataType: 'numeric' },
  { id: 'incident_count', label: 'Incident Count', fieldType: 'predefined', dataType: 'numeric' },
];

const REQUIRED_FIELD_IDS = SYSTEM_FIELDS.filter((f) => f.required).map((f) => f.id);
const STEP_LABELS = ['Upload & Map', 'Preview', 'Match Results', 'Feedback'];

function SearchableFieldSelect({
  value,
  onChange,
  fields,
  usedFieldIds,
  isRequired,
}: {
  value: string;
  onChange: (val: string) => void;
  fields: FieldOption[];
  usedFieldIds: Set<string>;
  isRequired: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropUp, setDropUp] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggle = () => {
    if (!open && wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < 280);
    }
    setOpen(!open);
    setSearch('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const selectedField = fields.find((f) => f.id === value);
  const displayLabel = value === '__custom__' ? 'Custom Field' : selectedField ? selectedField.label : "Don't import";

  const norm = search.toLowerCase();
  const filtered = fields.filter((f) => {
    if (!norm) return true;
    return f.label.toLowerCase().includes(norm) || f.id.toLowerCase().includes(norm);
  });

  return (
    <div className={`ew-search-select ${open ? 'open' : ''} ${isRequired ? 'is-required' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={`ew-ss-trigger ${value ? 'has-value' : ''}`}
        onClick={handleToggle}
      >
        <span className="ew-ss-trigger-label">{displayLabel}</span>
        <HiChevronUpDown className="ew-ss-trigger-icon" />
      </button>
      {open && (
        <div className={`ew-ss-dropdown ${dropUp ? 'ew-ss-dropdown-up' : ''}`}>
          <div className="ew-ss-search-wrap">
            <input
              ref={inputRef}
              className="ew-ss-search"
              type="text"
              placeholder="Search fields..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="ew-ss-options">
            <div
              className={`ew-ss-option ${!value ? 'selected' : ''}`}
              onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
            >
              Don't import
            </div>
            {filtered.map((f) => {
              const isUsed = usedFieldIds.has(f.id) && value !== f.id;
              return (
                <div
                  key={f.id}
                  className={`ew-ss-option ${value === f.id ? 'selected' : ''} ${isUsed ? 'disabled' : ''}`}
                  onClick={() => {
                    if (isUsed) return;
                    onChange(f.id);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <span>{f.label}{f.required ? ' *' : ''}</span>
                  {isUsed && <span className="ew-ss-option-tag">mapped</span>}
                  {value === f.id && <HiCheck className="ew-ss-option-check" />}
                </div>
              );
            })}
            <div
              className={`ew-ss-option ${value === '__custom__' ? 'selected' : ''}`}
              onClick={() => { onChange('__custom__'); setOpen(false); setSearch(''); }}
            >
              <span>Custom Field (use column name)</span>
              {value === '__custom__' && <HiCheck className="ew-ss-option-check" />}
            </div>
            {filtered.length === 0 && search && (
              <div className="ew-ss-no-results">No fields match &quot;{search}&quot;</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ImportDialog({ isOpen, onClose, onImportComplete, brandName, brandId, isUltimateParent }: ImportDialogProps) {
  const user = useAuthStore((s) => s.user);

  // ── Dialog view: history or wizard ──
  const [view, setView] = useState<DialogView>('history');

  // ══════════════════════════════
  //  HISTORY VIEW STATE
  // ══════════════════════════════
  const [imports, setImports] = useState<ImportRecord[]>([]);
  const [importsLoading, setImportsLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<'matched' | 'unmatched'>('matched');
  const [unmatchedStores, setUnmatchedStores] = useState<any[]>([]);
  const [matchedStores, setMatchedStores] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // ══════════════════════════════
  //  WIZARD VIEW STATE
  // ══════════════════════════════
  const [step, setStep] = useState<WizardStep>(1);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvSampleValues, setCsvSampleValues] = useState<Record<string, string[]>>({});
  const [csvPreview, setCsvPreview] = useState<Record<string, string>[] | null>(null);
  const [parsing, setParsing] = useState(false);
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [processing, setProcessing] = useState(false);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [matchResults, setMatchResults] = useState<MatchResults | null>(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ══════════════════════════════
  //  HISTORY LOGIC
  // ══════════════════════════════
  const loadHistory = useCallback(async () => {
    if (!brandId) return;
    setImportsLoading(true);
    try {
      const data = await getImportHistory(brandId);
      setImports(data);
    } catch (err) {
      console.error('Failed to load import history:', err);
    }
    setImportsLoading(false);
  }, [brandId]);

  useEffect(() => {
    if (isOpen) loadHistory();
  }, [isOpen, loadHistory]);

  const handleRowExpand = async (imp: ImportRecord) => {
    if (expandedId === imp.id) { setExpandedId(null); return; }
    setExpandedId(imp.id);
    setDetailTab('matched');
    setDetailLoading(true);
    setMatchedStores([]);
    setUnmatchedStores([]);
    try {
      const [unmatched, dedupResponse] = await Promise.all([
        getUnmatchedStores(imp.id),
        getUploadDedupResponse(imp.id),
      ]);
      setUnmatchedStores(unmatched);
      if (dedupResponse?.matches) setMatchedStores(dedupResponse.matches);
      else if (Array.isArray(dedupResponse)) setMatchedStores(dedupResponse);
      else setMatchedStores([]);
    } catch (err) { console.error('Failed to load store details:', err); }
    setDetailLoading(false);
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const formatShortDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getMatchPercentage = (imp: ImportRecord) => {
    const matched = imp.matched_count ?? 0;
    const unmatched = imp.unmatched_count ?? 0;
    const total = matched + unmatched;
    if (total === 0) return 0;
    return Math.round((matched / total) * 100);
  };

  const renderStars = (r: number) =>
    Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`idlg-star ${i < r ? 'idlg-star-filled' : ''}`}>&#9733;</span>
    ));

  // ══════════════════════════════
  //  WIZARD LOGIC
  // ══════════════════════════════
  const normalizeStr = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

  const getFieldMeta = (fieldId: string) => {
    const found = SYSTEM_FIELDS.find((f) => f.id === fieldId);
    if (found) return { fieldType: found.fieldType, dataType: found.dataType, matchOnly: !!found.matchOnly };
    return { fieldType: 'dynamic' as const, dataType: 'string', matchOnly: false };
  };

  const autoMatchHeader = (header: string): string => {
    const norm = normalizeStr(header);
    // Strip common prefixes like "gk", "gkaccount" so "GK Account Penetration" → compare as "penetration"
    const stripped = norm.replace(/^gkaccount/, '').replace(/^gk/, '');

    // Direct match by field id or label (normalized)
    const direct = SYSTEM_FIELDS.find((f) => {
      const nId = normalizeStr(f.id);
      const nLabel = normalizeStr(f.label);
      return nId === norm || nLabel === norm || nId === stripped || nLabel === stripped;
    });
    if (direct) return direct.id;

    if (norm.includes('zip') || norm.includes('postal')) return 'zipcode';
    if (norm.includes('addr') || norm.includes('street')) return 'address';
    if (norm.includes('brand')) return 'brand_name';
    // Store Number: "store number", "store no", "store #", "store id", "storeid", "store_num" etc.
    if (norm.includes('store') && (norm.includes('num') || norm.includes('no') || norm.includes('id') || norm === 'store')) return 'store_number';
    // POI
    if (norm.includes('customer') && norm.includes('id')) return 'customer_id';
    if (norm === 'sgid' || (norm.includes('sg') && norm.includes('id') && !norm.includes('store'))) return 'sg_id';
    if (norm.includes('lat') && !norm.includes('late')) return 'latitude';
    if (norm.includes('lng') || norm.includes('lon')) return 'longitude';
    if (norm.includes('open') && norm.includes('date')) return 'opened_on';
    if (norm.includes('close') && norm.includes('date')) return 'closed_on';
    if (norm.includes('purchek') && norm.includes('install')) return 'purchek_install_date';
    if (norm.includes('containment') && norm.includes('install')) return 'containment_install_date';
    if ((norm.includes('cartmanager') || (norm.includes('cart') && norm.includes('manager'))) && norm.includes('install')) return 'cart_manager_install_date';
    if (norm.includes('camera') && norm.includes('install')) return 'camera_install_date';
    if (norm.includes('subscription') && norm.includes('start')) return 'subscription_start_date';
    if ((norm.includes('pairedwheel') || (norm.includes('paired') && norm.includes('wheel'))) && norm.includes('install')) return 'paired_wheel_install_date';
    if (norm.includes('combo') && norm.includes('start')) return 'combo_start_date';
    if (norm.includes('lifetime') && norm.includes('val')) return 'lifetime_value';
    if ((norm.includes('cartmanager') || (norm.includes('cart') && norm.includes('manager'))) && norm.includes('rank')) return 'cart_manager_rank';
    if (norm.includes('purchek') && norm.includes('rank')) return 'purchek_rank';
    if (norm.includes('containment') && norm.includes('rank')) return 'containment_rank';
    if (norm === 'rank' || norm === 'overallrank') return 'rank';
    if (norm.includes('last') && norm.includes('purchase')) return 'last_purchase';
    if (norm.includes('first') && norm.includes('purchase')) return 'first_purchase';
    if (norm.includes('risk') && norm.includes('score')) return 'risk_score';
    if (norm.includes('risk') && norm.includes('index')) return 'risk_index';
    if (norm.includes('shrink') && norm.includes('count')) return 'shrink_incident_count';
    if (norm.includes('shrink') && norm.includes('val')) return 'shrink_incident_value';
    // Geo
    if (norm === 'city') return 'city';
    if (norm === 'state' || norm === 'statename') return 'state';
    if (norm === 'statecode' || norm === 'stateabbr') return 'state_code';
    if (norm === 'country') return 'country';
    if (norm === 'county') return 'county';
    if (norm.includes('containment') && norm.includes('req')) return 'containment_required';
    if (norm.includes('penalty') && norm.includes('cart')) return 'penalty_for_cart_owner';
    if (norm.includes('law') && norm.includes('summary')) return 'law_summary';
    if (norm.includes('law') && norm.includes('source')) return 'law_source';
    if (norm.includes('crime') && norm.includes('incident')) return 'total_crime_incidents';
    if (norm.includes('crime') && norm.includes('rate')) return 'crime_rate';
    if (norm.includes('crime') && norm.includes('data')) return 'crime_data';
    if (norm.includes('cold') && norm.includes('day')) return 'no_of_cold_days';
    if (norm.includes('hot') && norm.includes('day')) return 'no_of_hot_days';
    if (norm.includes('precip')) return 'annual_precipitation';
    // Nearby Penetration — use stripped to handle "GK Account Penetration" prefix
    const pen = stripped || norm;
    if (pen.includes('totalneighbor') || norm.includes('totalneighbor')) return 'total_neighbors';
    if (pen.includes('purchek') && pen.includes('customer') && pen.includes('count')) return 'purchek_customer_count';
    if (pen.includes('containment') && pen.includes('customer') && pen.includes('count')) return 'containment_customer_count';
    if (pen.includes('combo') && pen.includes('customer') && pen.includes('count')) return 'combo_customer_count';
    if (pen.includes('penetration') && pen.includes('purchek')) return 'penetration_purchek';
    if (pen.includes('penetration') && pen.includes('containment')) return 'penetration_containment';
    if (pen.includes('penetration') && pen.includes('combo')) return 'penetration_combo';
    if (pen.includes('penetration') && pen.includes('paired')) return 'penetration_paired_wheel';
    if (pen === 'penetration' || pen === 'overallpenetration' || norm === 'gkaccountpenetration' || norm === 'accountpenetration') return 'penetration';
    if (pen.includes('plaza') && pen.includes('cm')) return 'plaza_cm_customer_count';
    if (pen.includes('plaza') && pen.includes('cluster')) return 'plaza_cluster';
    if ((pen.includes('pairedwheel') || (pen.includes('paired') && pen.includes('wheel'))) && pen.includes('customer')) return 'paired_wheel_customer_count';
    if (pen.includes('shoplift') && pen.includes('amount')) return 'shoplifting_amount_usd';
    if (pen.includes('shoplift') && pen.includes('incident')) return 'shoplifting_incident_count';
    if (pen.includes('highway') && pen.includes('prox')) return 'highway_proximity';
    if (pen.includes('totaldensity') || (pen.includes('total') && pen.includes('density'))) return 'total_density';
    if (pen.includes('busstop') || (pen.includes('bus') && pen.includes('prox'))) return 'busstop_proximity';
    // Video
    if (norm.includes('totalcrime') && norm.includes('amount')) return 'total_crime_amount_usd';
    if (norm === 'incidentcount' || (norm.includes('incident') && norm.includes('count') && !norm.includes('shrink') && !norm.includes('shoplift') && !norm.includes('crime'))) return 'incident_count';
    return '';
  };

  const usedFieldIds = useMemo(() => {
    const used = new Set<string>();
    Object.values(columnMappings).forEach((v) => { if (v && v !== '__custom__') used.add(v); });
    return used;
  }, [columnMappings]);

  const requiredFieldsMapped = useMemo(() =>
    REQUIRED_FIELD_IDS.every((reqId) => usedFieldIds.has(reqId)),
  [usedFieldIds]);

  const mappedCount = useMemo(() =>
    Object.values(columnMappings).filter((v) => v !== '').length,
  [columnMappings]);

  const unmappedRequired = REQUIRED_FIELD_IDS.filter((id) => !usedFieldIds.has(id));

  // CSV parsing
  const parseCSVLine = (line: string): string[] => {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && i + 1 < line.length && line[i + 1] === '"') { current += '"'; i++; }
        else if (ch === '"') inQuotes = false;
        else current += ch;
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === ',') { fields.push(current.trim()); current = ''; }
        else current += ch;
      }
    }
    fields.push(current.trim());
    return fields;
  };

  const processTextAsCSV = (text: string) => {
    const lines = text.split('\n').filter((l) => l.trim());
    if (lines.length < 2) { setError('File must have a header row and at least one data row.'); setParsing(false); return; }
    const headers = parseCSVLine(lines[0]);
    setCsvHeaders(headers);
    const samples: Record<string, string[]> = {};
    const rows: Record<string, string>[] = [];
    for (let i = 1; i < Math.min(lines.length, 6); i++) {
      const vals = parseCSVLine(lines[i]);
      const row: Record<string, string> = {};
      headers.forEach((h, idx) => {
        const val = vals[idx] || '';
        row[h] = val;
        if (i <= 3) { if (!samples[h]) samples[h] = []; if (val) samples[h].push(val); }
      });
      rows.push(row);
    }
    setCsvSampleValues(samples);
    setCsvPreview(rows);
    const autoMappings: Record<string, string> = {};
    const alreadyUsed = new Set<string>();
    headers.forEach((h) => {
      const match = autoMatchHeader(h);
      if (match && !alreadyUsed.has(match)) { autoMappings[h] = match; alreadyUsed.add(match); }
      else autoMappings[h] = '';
    });
    setColumnMappings(autoMappings);
    setParsing(false);
  };

  const parseFile = (file: File) => {
    setParsing(true);
    if (isExcelFile(file.name)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const csvText = XLSX.utils.sheet_to_csv(sheet);
          processTextAsCSV(csvText);
        } catch (err: any) {
          setError('Failed to parse Excel file: ' + (err.message || 'Unknown error'));
          setParsing(false);
        }
      };
      reader.onerror = () => { setError('Failed to read file.'); setParsing(false); };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => { processTextAsCSV(e.target?.result as string); };
      reader.onerror = () => { setError('Failed to read file.'); setParsing(false); };
      reader.readAsText(file);
    }
  };

  const ALLOWED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];
  const isExcelFile = (name: string) => /\.(xlsx|xls)$/i.test(name);

  const validateFile = (file: File): string | null => {
    const ext = file.name.toLowerCase();
    if (!ALLOWED_EXTENSIONS.some((e) => ext.endsWith(e))) return 'Only .csv, .xlsx, or .xls files are accepted.';
    if (file.size > 50 * 1024 * 1024) return 'File size exceeds 50MB limit.';
    if (file.size === 0) return 'File is empty.';
    return null;
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false); setError(null);
    const file = e.dataTransfer.files?.[0];
    if (file) { const err = validateFile(file); if (err) { setError(err); return; } setSelectedFile(file); parseFile(file); }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (file) { const err = validateFile(file); if (err) { setError(err); return; } setSelectedFile(file); parseFile(file); }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null); setCsvHeaders([]); setCsvSampleValues({}); setCsvPreview(null); setColumnMappings({}); setError(null);
  };

  const updateMapping = (csvHeader: string, fieldId: string) => {
    setColumnMappings((prev) => ({ ...prev, [csvHeader]: fieldId }));
  };

  const excelToCSVFile = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const csvText = XLSX.utils.sheet_to_csv(sheet);
          const csvFilename = file.name.replace(/\.(xlsx|xls)$/i, '.csv');
          resolve(new File([csvText], csvFilename, { type: 'text/csv' }));
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  // Pipeline
  const handleRunPipeline = async () => {
    if (!selectedFile) return;
    setProcessing(true); setError(null);
    try {
      let fileToUpload: File = selectedFile;
      if (isExcelFile(selectedFile.name)) {
        fileToUpload = await excelToCSVFile(selectedFile);
      }
      const uploadResult = await uploadCSVDirect(fileToUpload, brandId, user?.id || 'unknown', isUltimateParent ? brandId : undefined);
      const csvUploadId = uploadResult.upload_id;
      setUploadId(csvUploadId);

      const mappings: Array<{ csv_column_name: string; mapped_to_field: string; field_type: string; data_type: string }> = [];
      for (const [csvCol, fieldId] of Object.entries(columnMappings)) {
        if (!fieldId) continue;
        if (fieldId === '__custom__') {
          mappings.push({ csv_column_name: csvCol, mapped_to_field: csvCol.toLowerCase().replace(/[^a-z0-9]+/g, '_'), field_type: 'dynamic', data_type: 'string' });
        } else {
          const meta = getFieldMeta(fieldId);
          if (!meta.matchOnly) mappings.push({ csv_column_name: csvCol, mapped_to_field: fieldId, field_type: meta.fieldType, data_type: meta.dataType });
        }
      }

      if (mappings.length > 0) {
        const validationResult = await validateCSVMapping(csvUploadId, mappings);
        if (!validationResult.valid) { setError(`Mapping validation failed: ${validationResult.errors.join('; ')}`); setProcessing(false); return; }
      }

      const dedupResult = await dedupStores(csvUploadId);
      let enrichResult = null;
      if (dedupResult.matched_count > 0 && mappings.length > 0) {
        try { enrichResult = await enrichStores(csvUploadId); } catch (err) { console.error('Enrichment failed (non-blocking):', err); }
      }

      setMatchResults({
        matchRate: dedupResult.match_percentage || 0,
        matched: dedupResult.matched_count || 0,
        unmatched: dedupResult.unmatched_count || 0,
        totalRows: uploadResult.row_count || 0,
        enrichResults: enrichResult || undefined,
      });
      setStep(3);
      onImportComplete?.();
    } catch (err: any) {
      console.error('Pipeline error:', err);
      setError(err?.response?.errors?.[0]?.message || err?.message || 'An error occurred during processing.');
    } finally { setProcessing(false); }
  };

  const handleSubmitFeedback = async () => {
    if (!uploadId || !user?.id || rating === 0) return;
    setSubmitting(true);
    try {
      await submitFeedback(uploadId, user.id, rating, feedback.trim() || undefined);
      setSubmitted(true);
      setTimeout(() => { resetWizard(); loadHistory(); }, 1500);
    } catch (err) {
      console.error('Feedback submission failed:', err);
      setSubmitted(true);
      setTimeout(() => { resetWizard(); loadHistory(); }, 1500);
    } finally { setSubmitting(false); }
  };

  const canNext = useMemo(() => {
    if (step === 1) return !!csvPreview && csvHeaders.length > 0 && requiredFieldsMapped;
    if (step === 2) return !!csvPreview && !processing;
    if (step === 3) return !!matchResults;
    return true;
  }, [step, csvPreview, csvHeaders, requiredFieldsMapped, processing, matchResults]);

  const goNext = () => {
    if (step === 1) { setStep(2); return; }
    if (step === 2) { handleRunPipeline(); return; }
    if (step === 3) { setStep(4); return; }
  };

  const goBack = () => {
    if (step > 1) setStep((s) => (s - 1) as WizardStep);
  };

  const resetWizard = () => {
    setView('history');
    setStep(1);
    setSelectedFile(null); setCsvHeaders([]); setCsvSampleValues({}); setCsvPreview(null);
    setColumnMappings({}); setError(null); setProcessing(false); setUploadId(null);
    setMatchResults(null); setRating(0); setHoverRating(0); setFeedback('');
    setSubmitted(false); setSubmitting(false); setDragActive(false);
  };

  const startWizard = () => {
    resetWizard();
    setView('wizard');
  };

  // Donut chart
  const DonutChart = ({ percent }: { percent: number }) => {
    const r = 54;
    const c = 2 * Math.PI * r;
    const offset = c - (percent / 100) * c;
    return (
      <svg className="ew-donut" viewBox="0 0 128 128">
        <circle cx="64" cy="64" r={r} fill="none" stroke="#e5e7eb" strokeWidth="12" />
        <circle cx="64" cy="64" r={r} fill="none" stroke="#10b981" strokeWidth="12" strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" transform="rotate(-90 64 64)" />
        <text x="64" y="58" textAnchor="middle" className="ew-donut-pct">{percent}%</text>
        <text x="64" y="76" textAnchor="middle" className="ew-donut-label">Match Rate</text>
      </svg>
    );
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="idlg-overlay" onClick={(e) => { if (e.target === e.currentTarget && !processing) onClose(); }}>
      <div className="idlg-container">
        {/* Header */}
        <div className="idlg-header">
          <div className="idlg-header-left">
            {view === 'wizard' && (
              <button className="idlg-header-back" onClick={() => { if (!processing) resetWizard(); }}>
                <HiArrowLeft />
              </button>
            )}
            <h2>{view === 'history' ? 'Import Data' : 'Enrich Data'}</h2>
            <span className="idlg-header-brand">{toTitleCase(brandName)}</span>
          </div>
          <button className="idlg-close" onClick={() => { if (!processing) onClose(); }}><HiXMark /></button>
        </div>

        {/* ═══ WIZARD: Step Bar ═══ */}
        {view === 'wizard' && (
          <div className="ew-steps">
            {STEP_LABELS.map((label, i) => {
              const stepNum = (i + 1) as WizardStep;
              const isActive = step === stepNum;
              const isDone = step > stepNum;
              return (
                <div key={label} className={`ew-step ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}>
                  <div className="ew-step-circle">{isDone ? <HiCheck /> : stepNum}</div>
                  <span className="ew-step-label">{label}</span>
                  {i < STEP_LABELS.length - 1 && <div className="ew-step-line" />}
                </div>
              );
            })}
          </div>
        )}

        {/* Body */}
        <div className="idlg-body">
          {/* ═══════════════════════════════
              HISTORY VIEW
             ═══════════════════════════════ */}
          {view === 'history' && (
            <>
              <div className="idlg-actions">
                <button className="idlg-enrich-btn" onClick={startWizard}>
                  <HiOutlineCloudUpload /> Enrich Data
                </button>
              </div>

              <div className="idlg-history">
                <h3 className="idlg-section-title">Import History</h3>
                {importsLoading ? (
                  <div className="idlg-loading"><Spinner size="small" /></div>
                ) : imports.length === 0 ? (
                  <div className="idlg-empty">
                    <HiOutlineDocumentText className="idlg-empty-icon" />
                    <h4>No imports yet</h4>
                    <p>Upload a CSV or Excel file to start importing store data.</p>
                  </div>
                ) : (
                  <div className="idlg-table">
                    <div className="idlg-table-head">
                      <span className="idlg-th idlg-col-file">File</span>
                      <span className="idlg-th idlg-col-uploaded">Uploaded by</span>
                      <span className="idlg-th idlg-col-date">Date</span>
                      <span className="idlg-th idlg-col-stores">Stores</span>
                      <span className="idlg-th idlg-col-match">Match result</span>
                      <span className="idlg-th idlg-col-feedback">Feedback</span>
                      <span className="idlg-th idlg-col-chevron"></span>
                    </div>
                    {imports.map((imp) => {
                      const isExpanded = expandedId === imp.id;
                      const matchPct = getMatchPercentage(imp);
                      const matched = imp.matched_count ?? 0;
                      const unmatched = imp.unmatched_count ?? 0;
                      const feedbackRating = imp.feedback?.[0]?.rating ?? 0;
                      return (
                        <div key={imp.id}>
                          <div
                            className={`idlg-table-row idlg-row-clickable ${isExpanded ? 'idlg-row-expanded' : ''}`}
                            onClick={() => handleRowExpand(imp)}
                          >
                            <span className="idlg-td idlg-col-file">
                              <div className="idlg-file-icon-wrap"><HiOutlineDocumentText className="idlg-file-icon" /></div>
                              <div className="idlg-file-info">
                                <span className="idlg-file-name">{imp.original_filename || 'Untitled'}</span>
                                {imp.file_size_bytes ? <span className="idlg-file-size">{formatFileSize(imp.file_size_bytes)}</span> : null}
                              </div>
                            </span>
                            <span className="idlg-td idlg-col-uploaded">{imp.uploader?.name || imp.uploader?.email?.split('@')[0] || imp.uploader_id}</span>
                            <span className="idlg-td idlg-col-date">{formatShortDate(imp.upload_timestamp)}</span>
                            <span className="idlg-td idlg-col-stores">{(imp.row_count || 0).toLocaleString()}</span>
                            <span className="idlg-td idlg-col-match">
                              {imp.status === 'processing' ? (
                                <span className="idlg-badge idlg-status-processing">Processing</span>
                              ) : imp.status === 'failed' ? (
                                <span className="idlg-badge idlg-status-failed">Failed</span>
                              ) : (matched + unmatched) > 0 ? (
                                <div className="idlg-match-info">
                                  <div className="idlg-match-bar-row">
                                    <div className="idlg-match-bar"><div className="idlg-match-bar-fill" style={{ width: `${matchPct}%` }} /></div>
                                    <span className="idlg-match-pct">{matchPct}%</span>
                                  </div>
                                  <div className="idlg-match-counts">
                                    <span className="idlg-match-count-matched">{matched} matched</span>
                                    <span className="idlg-match-count-unmatched">{unmatched} unmatched</span>
                                  </div>
                                </div>
                              ) : <span className="idlg-match-na">-</span>}
                            </span>
                            <span className="idlg-td idlg-col-feedback">
                              {feedbackRating > 0 ? <div className="idlg-stars">{renderStars(feedbackRating)}</div> : <span className="idlg-match-na">-</span>}
                            </span>
                            <span className="idlg-td idlg-col-chevron">
                              {isExpanded ? <HiOutlineChevronUp /> : <HiOutlineChevronDown />}
                            </span>
                          </div>
                          {isExpanded && (
                            <div className="idlg-detail-panel">
                              <div className="idlg-detail-tabs">
                                <button className={`idlg-detail-tab ${detailTab === 'matched' ? 'active' : ''}`} onClick={() => setDetailTab('matched')}>Matched ({matched})</button>
                                <button className={`idlg-detail-tab ${detailTab === 'unmatched' ? 'active' : ''}`} onClick={() => setDetailTab('unmatched')}>Unmatched ({unmatched})</button>
                              </div>
                              <div className="idlg-detail-body">
                                {detailLoading ? (
                                  <div className="idlg-loading"><Spinner size="small" /></div>
                                ) : detailTab === 'matched' ? (
                                  matchedStores.length === 0 ? <div className="idlg-detail-empty">No matched stores found.</div> : (
                                    <div className="idlg-detail-table">
                                      <div className="idlg-detail-head">
                                        <span className="idlg-dth" style={{ flex: 0.5 }}>#</span>
                                        <span className="idlg-dth" style={{ flex: 2 }}>Input Address</span>
                                        <span className="idlg-dth" style={{ flex: 2 }}>Matched Store Address</span>
                                      </div>
                                      {matchedStores.map((m: any, idx: number) => (
                                        <div key={idx} className="idlg-detail-row">
                                          <span className="idlg-dtd" style={{ flex: 0.5 }}>{idx + 1}</span>
                                          <span className="idlg-dtd" style={{ flex: 2 }}>{m.c_address || '-'}</span>
                                          <span className="idlg-dtd" style={{ flex: 2 }}>{m.s_address || '-'}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )
                                ) : (
                                  unmatchedStores.length === 0 ? <div className="idlg-detail-empty">No unmatched stores found.</div> : (
                                    <div className="idlg-detail-table">
                                      <div className="idlg-detail-head">
                                        <span className="idlg-dth" style={{ flex: 0.5 }}>#</span>
                                        <span className="idlg-dth" style={{ flex: 3 }}>Input Address</span>
                                      </div>
                                      {unmatchedStores.map((u: any, idx: number) => (
                                        <div key={u.id} className="idlg-detail-row">
                                          <span className="idlg-dtd" style={{ flex: 0.5 }}>{idx + 1}</span>
                                          <span className="idlg-dtd" style={{ flex: 3 }}>{u.address || '-'}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ═══════════════════════════════
              WIZARD VIEW
             ═══════════════════════════════ */}
          {view === 'wizard' && (
            <>
              {/* Step 1: Upload & Map */}
              {step === 1 && (
                <div className="ew-s1">
                  {!selectedFile ? (
                    <div className={`ew-dropzone ${dragActive ? 'drag-active' : ''}`} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
                      <HiOutlineCloudUpload className="ew-dz-icon" />
                      <p className="ew-dz-text">Drag and drop your file here</p>
                      <p className="ew-dz-sub">or</p>
                      <label className="ew-dz-browse">Browse Files<input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} hidden /></label>
                      <p className="ew-dz-hint">CSV or Excel files, max 50MB</p>
                    </div>
                  ) : (
                    <div className="ew-file-bar">
                      <HiOutlineDocumentText className="ew-fb-icon" />
                      <div className="ew-fb-info">
                        <span className="ew-fb-name">{selectedFile.name}</span>
                        <span className="ew-fb-size">{(selectedFile.size / 1024).toFixed(1)} KB</span>
                      </div>
                      <button className="ew-fb-remove" onClick={handleRemoveFile}>Remove</button>
                    </div>
                  )}

                  {error && <div className="ew-error"><HiOutlineExclamationCircle /><span>{error}</span></div>}
                  {parsing && <div className="ew-loading"><Spinner size="small" /></div>}

                  {csvHeaders.length > 0 && !parsing && (
                    <div className="ew-mapping">
                      <div className="ew-mapping-header">
                        <h4>Map CSV Columns</h4>
                        <span className="ew-mapping-count">{mappedCount} of {csvHeaders.length} columns mapped</span>
                      </div>
                      {unmappedRequired.length > 0 && (
                        <div className="ew-mapping-warn">
                          <HiOutlineExclamationCircle />
                          <span>Required fields not mapped: {unmappedRequired.map((id) => SYSTEM_FIELDS.find((f) => f.id === id)?.label || id).join(', ')}</span>
                        </div>
                      )}
                      <div className="ew-mapping-table-wrap">
                        <table className="ew-mapping-table">
                          <thead><tr><th>CSV Column</th><th>Sample Values</th><th>Map To</th></tr></thead>
                          <tbody>
                            {csvHeaders.map((header) => {
                              const currentMapping = columnMappings[header] || '';
                              const samples = csvSampleValues[header] || [];
                              const isRequired = REQUIRED_FIELD_IDS.includes(currentMapping);
                              return (
                                <tr key={header} className={currentMapping ? 'mapped' : ''}>
                                  <td className="ew-mapping-col-name"><span>{header}</span></td>
                                  <td className="ew-mapping-sample"><span className="ew-mapping-sample-text">{samples.slice(0, 2).join(', ') || '-'}</span></td>
                                  <td className="ew-mapping-select-cell">
                                    <SearchableFieldSelect
                                      value={currentMapping}
                                      onChange={(val) => updateMapping(header, val)}
                                      fields={SYSTEM_FIELDS}
                                      usedFieldIds={usedFieldIds}
                                      isRequired={isRequired}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Preview */}
              {step === 2 && (
                <div className="ew-s2">
                  <div className="ew-s2-summary">
                    <div className="ew-s2-summary-item"><span className="ew-s2-summary-label">File</span><span className="ew-s2-summary-value">{selectedFile?.name}</span></div>
                    <div className="ew-s2-summary-item"><span className="ew-s2-summary-label">Mapped Columns</span><span className="ew-s2-summary-value">{mappedCount} of {csvHeaders.length}</span></div>
                  </div>
                  {error && <div className="ew-error"><HiOutlineExclamationCircle /><span>{error}</span></div>}
                  {csvPreview && (
                    <div className="ew-preview">
                      <div className="ew-preview-head"><h4>Preview (first 5 rows)</h4><span className="ew-preview-count">{csvPreview.length} rows shown</span></div>
                      <div className="ew-preview-scroll">
                        <table className="ew-preview-table">
                          <thead><tr>
                            {Object.keys(csvPreview[0]).map((h) => {
                              const mapped = columnMappings[h];
                              const field = mapped && mapped !== '__custom__' ? SYSTEM_FIELDS.find((f) => f.id === mapped) : null;
                              return (<th key={h}><div>{h}</div>{field && <div className="ew-preview-mapped-label">{field.label}</div>}{mapped === '__custom__' && <div className="ew-preview-mapped-label">Custom Field</div>}{!mapped && <div className="ew-preview-unmapped-label">Skipped</div>}</th>);
                            })}
                          </tr></thead>
                          <tbody>
                            {csvPreview.map((row, i) => (
                              <tr key={i}>{Object.entries(row).map(([h, v], j) => <td key={j} className={!columnMappings[h] ? 'skipped' : ''}>{v}</td>)}</tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Match Results */}
              {step === 3 && (
                <div className="ew-s3">
                  {matchResults ? (
                    <>
                      <div className="ew-s3-top">
                        <div className="ew-s3-donut"><DonutChart percent={matchResults.matchRate} /></div>
                        <div className="ew-s3-cards">
                          <div className="ew-s3-card green"><span className="ew-s3-card-value">{matchResults.matched.toLocaleString()}</span><span className="ew-s3-card-label">Matched Stores</span></div>
                          <div className="ew-s3-card blue"><span className="ew-s3-card-value">{matchResults.unmatched.toLocaleString()}</span><span className="ew-s3-card-label">Unmatched Stores</span></div>
                          <div className="ew-s3-card gray"><span className="ew-s3-card-value">{matchResults.totalRows.toLocaleString()}</span><span className="ew-s3-card-label">Total in CSV</span></div>
                        </div>
                      </div>
                      {matchResults.enrichResults && (
                        <div className="ew-s3-updates">
                          <h4>Enrichment Summary</h4>
                          <div className="ew-s3-update-list">
                            <div className="ew-s3-update-row"><span className="ew-s3-update-field">Stores Updated</span><div className="ew-s3-update-bar-bg"><div className="ew-s3-update-bar" style={{ width: `${matchResults.matched > 0 ? Math.round((matchResults.enrichResults.updated_count / matchResults.matched) * 100) : 0}%` }} /></div><span className="ew-s3-update-count">{matchResults.enrichResults.updated_count}/{matchResults.matched}</span></div>
                            <div className="ew-s3-update-row"><span className="ew-s3-update-field">Predefined Fields</span><div className="ew-s3-update-bar-bg"><div className="ew-s3-update-bar" style={{ width: `${matchResults.enrichResults.predefined_enriched > 0 ? '100' : '0'}%` }} /></div><span className="ew-s3-update-count">{matchResults.enrichResults.predefined_enriched}</span></div>
                            <div className="ew-s3-update-row"><span className="ew-s3-update-field">Dynamic Fields</span><div className="ew-s3-update-bar-bg"><div className="ew-s3-update-bar" style={{ width: `${matchResults.enrichResults.dynamic_enriched > 0 ? '100' : '0'}%` }} /></div><span className="ew-s3-update-count">{matchResults.enrichResults.dynamic_enriched}</span></div>
                            {matchResults.enrichResults.failed_count > 0 && (
                              <div className="ew-s3-update-row"><span className="ew-s3-update-field">Failed</span><div className="ew-s3-update-bar-bg"><div className="ew-s3-update-bar" style={{ width: '100%', background: '#ef4444' }} /></div><span className="ew-s3-update-count">{matchResults.enrichResults.failed_count}</span></div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="ew-s3-running"><HiArrowPath className="ew-spin" /><h3>Processing...</h3><p>Matching stores and enriching data.</p></div>
                  )}
                </div>
              )}

              {/* Step 4: Feedback */}
              {step === 4 && (
                <div className="ew-s4">
                  {!submitted ? (
                    <>
                      <div className="ew-s4-done-icon"><HiOutlineCheckCircle /></div>
                      <h3>Import Complete!</h3>
                      <p>Your data has been enriched successfully. How was your experience?</p>
                      <div className="ew-s4-stars">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <button key={s} className={`ew-star ${(hoverRating || rating) >= s ? 'filled' : ''}`} onClick={() => setRating(s)} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)}>
                            {(hoverRating || rating) >= s ? <HiStar /> : <HiOutlineStar />}
                          </button>
                        ))}
                      </div>
                      {rating > 0 && rating <= 3 && <textarea className="ew-s4-textarea" placeholder="What could we improve?" value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3} />}
                    </>
                  ) : (
                    <div className="ew-s4-submitted"><HiOutlineCheckCircle className="ew-s4-submitted-icon" /><h3>Thank you!</h3><p>Your feedback has been recorded.</p></div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer (wizard only) */}
        {view === 'wizard' && (
          <div className="ew-footer">
            <button className="ew-footer-cancel" onClick={() => { if (!processing) resetWizard(); }}>Cancel</button>
            <div className="ew-footer-right">
              {step > 1 && step < 4 && !processing && <button className="ew-footer-back" onClick={goBack}>Back</button>}
              {step < 4 ? (
                <button className="ew-footer-next" onClick={goNext} disabled={!canNext || processing}>
                  {step === 2 ? (processing ? <><span className="ew-btn-spinner" /> Processing...</> : 'Upload & Match') : step === 3 ? 'Continue' : 'Next'}
                </button>
              ) : (
                !submitted && <button className="ew-footer-next" onClick={handleSubmitFeedback} disabled={rating === 0 || submitting}>{submitting ? <><span className="ew-btn-spinner" /> Submitting...</> : 'Submit & Finish'}</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default ImportDialog;
